"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateSlots = void 0;
const constants_1 = require("@/app/shared/constants");
const numbers_1 = require("@/app/shared/constants/numbers");
const helpers_1 = require("@/app/shared/helpers");
const utils_1 = require("@/app/shared/helpers/utils");
const constants_2 = require("@/app/SUMOCEP/store/scheduler/constants");
const parseSlotIntervalGranularity = (slotIntervalGranularity, durationInMs) => {
    if (!slotIntervalGranularity || slotIntervalGranularity === 'APPOINTMENT_DURATION') {
        return durationInMs;
    }
    const granularityMinutes = parseInt(slotIntervalGranularity, 10);
    if (isNaN(granularityMinutes) || granularityMinutes <= 0) {
        return numbers_1.SLOTS_DURATION_MARGIN_MS;
    }
    return granularityMinutes * 60 * 1000;
};
const createSlotGenerationConfig = (bookingPage, slotIntervalGranularity) => {
    const duration = bookingPage.when?.duration?.count ?? constants_2.DEFAULT_BOOKING_PAGE.when.duration.count;
    const timeUnit = bookingPage.when?.duration?.timeUnit ?? constants_2.DEFAULT_BOOKING_PAGE.when.duration.timeUnit;
    const bufferBefore = helpers_1.bufferTimeConvert.fromStringToNumber(bookingPage.when?.beforeStartTime);
    const bufferAfter = helpers_1.bufferTimeConvert.fromStringToNumber(bookingPage.when?.afterEndTime);
    const durationInMs = (0, utils_1.convertTimeToMsWithTimeUnit)(duration, timeUnit);
    const bufferBeforeInMs = (0, utils_1.convertTimeToMsWithTimeUnit)(bufferBefore, constants_1.TimeUnit.MINUTE);
    const bufferAfterInMs = (0, utils_1.convertTimeToMsWithTimeUnit)(bufferAfter, constants_1.TimeUnit.MINUTE);
    const totalDurationInMs = durationInMs + bufferBeforeInMs + bufferAfterInMs;
    const slotIntervalGranularityInMs = parseSlotIntervalGranularity(slotIntervalGranularity, durationInMs);
    const currentDate = new Date();
    const currentDateTime = currentDate.getTime();
    const currentDayStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime();
    const currentDayEnd = currentDayStart + helpers_1.time.day;
    return {
        durationInMs,
        bufferBeforeInMs,
        bufferAfterInMs,
        totalDurationInMs,
        currentDateTime,
        currentDayStart,
        currentDayEnd,
        slotIntervalGranularityInMs,
    };
};
const canAddSlot = (slotTime, config) => {
    if (slotTime < config.currentDayStart) {
        return false;
    }
    if (slotTime >= config.currentDayStart && slotTime < config.currentDayEnd) {
        return slotTime >= config.currentDateTime;
    }
    return true;
};
const getDayFromTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};
const alignToGranularity = (timestamp, granularityMs, dayStart) => {
    const timeInDay = timestamp - dayStart;
    const alignedTimeInDay = Math.ceil(timeInDay / granularityMs) * granularityMs;
    return dayStart + alignedTimeInDay;
};
const addSlotsToDay = (dailySlotsMap, startTime, endTime, config) => {
    const dayKey = getDayFromTimestamp(startTime);
    if (!dailySlotsMap.has(dayKey)) {
        dailySlotsMap.set(dayKey, []);
    }
    const daySlots = dailySlotsMap.get(dayKey);
    const granularity = config.slotIntervalGranularityInMs;
    const earliestMeetingStart = startTime + config.bufferBeforeInMs;
    const alignedMeetingStart = alignToGranularity(earliestMeetingStart, granularity, dayKey);
    let currentTime = alignedMeetingStart - config.bufferBeforeInMs;
    while (currentTime + config.totalDurationInMs <= endTime && daySlots.length < numbers_1.SLOTS_MAX_DAYS_PER_DAY) {
        const slotTime = currentTime + config.bufferBeforeInMs;
        if (canAddSlot(slotTime, config)) {
            daySlots.push(slotTime);
        }
        currentTime += granularity;
    }
};
const generateSlotsForFreeSlot = (slot, dailySlotsMap, config) => {
    addSlotsToDay(dailySlotsMap, slot.startsAt, slot.finishesAt, config);
};
const generateSlotsForOccupiedSlot = (slot, overlappingMeetings, dailySlotsMap, config) => {
    overlappingMeetings.sort((a, b) => a.startsAt - b.startsAt);
    const firstMeeting = overlappingMeetings[0];
    if (firstMeeting && firstMeeting.startsAt - slot.startsAt >= config.totalDurationInMs) {
        addSlotsToDay(dailySlotsMap, slot.startsAt, firstMeeting.startsAt, config);
    }
    for (let i = 0; i < overlappingMeetings.length - 1; i++) {
        const currentMeeting = overlappingMeetings[i];
        const nextMeeting = overlappingMeetings[i + 1];
        if (currentMeeting && nextMeeting && nextMeeting.startsAt - currentMeeting.finishesAt >= config.totalDurationInMs) {
            addSlotsToDay(dailySlotsMap, currentMeeting.finishesAt, nextMeeting.startsAt, config);
        }
    }
    const lastMeeting = overlappingMeetings[overlappingMeetings.length - 1];
    if (lastMeeting && slot.finishesAt - lastMeeting.finishesAt >= config.totalDurationInMs) {
        addSlotsToDay(dailySlotsMap, lastMeeting.finishesAt, slot.finishesAt, config);
    }
};
const processAvailabilitySlot = (slot, hostSchedule, dailySlotsMap, config) => {
    if (slot.startsAt < config.currentDateTime && config.currentDateTime > slot.finishesAt)
        return;
    if (dailySlotsMap.size >= numbers_1.SLOTS_MAX_DAYS)
        return;
    const overlappingMeetings = hostSchedule.meetings.filter((meeting) => {
        return meeting.startsAt < slot.finishesAt && meeting.finishesAt > slot.startsAt;
    });
    if (overlappingMeetings.length === 0) {
        generateSlotsForFreeSlot(slot, dailySlotsMap, config);
    }
    else {
        generateSlotsForOccupiedSlot(slot, overlappingMeetings, dailySlotsMap, config);
    }
};
const collectSlotsFromMap = (dailySlotsMap) => {
    const spots = [];
    const sortedDays = Array.from(dailySlotsMap.keys()).sort();
    sortedDays.slice(0, numbers_1.SLOTS_MAX_DAYS).forEach((dayKey) => {
        const daySlots = dailySlotsMap.get(dayKey);
        spots.push(...daySlots.slice(0, numbers_1.SLOTS_MAX_DAYS_PER_DAY));
    });
    return spots;
};
const generateSlots = (bookingPage, hostSchedule, slotIntervalGranularity) => {
    if (!hostSchedule?.availability || !hostSchedule?.meetings || !bookingPage)
        return [];
    const config = createSlotGenerationConfig(bookingPage, slotIntervalGranularity);
    const dailySlotsMap = new Map();
    for (const slot of hostSchedule.availability) {
        processAvailabilitySlot(slot, hostSchedule, dailySlotsMap, config);
    }
    return collectSlotsFromMap(dailySlotsMap);
};
exports.generateSlots = generateSlots;
